import { format, isValid, eachDayOfInterval, getDay, differenceInMinutes, parse, isWithinInterval, addDays, eachMinuteOfInterval }from 'date-fns';
import { STATIC_OPTIONS } from './roleConstants';

export const getNestedError = (errors, path) => path.split('.').reduce((obj, key) => (obj && obj[key] ? obj[key] : undefined), errors);

export const safeFormat = (date, fmt) => {
  try {
    if (!date) return '';
    if (!isValid(date)) return '';
    return format(date, fmt);
  } catch {
    return '';
  }
};

export const transformToSelectOptions = (data, valueKey, labelKey) => {
    if (!data || !Array.isArray(data)) return [];
    return data.map(item => ({ value: item[valueKey], label: item[labelKey] }));
};

export const stripHtmlFromFrontend = (htmlString) => {
    if (!htmlString) return '';
    return htmlString.replace(/<[^>]*>/g, '').trim();
};

export const mapMultiSelect = (val) => Array.isArray(val) ? val.map(x => x.value).join(';') : (val || '');

// export const countWorkingDays = (startDate, endDate, weekDaysOff) => {
//     if (!isValid(startDate) || !isValid(endDate)) return 0;
//     const offIndices = (weekDaysOff || []).map(d => parseInt(d.value));
//     const workingDays = eachDayOfInterval({ start: startDate, end: endDate })
//         .filter(day => !offIndices.includes(getDay(day)));
//     return workingDays.length;
// };
export const countWorkingDays = (startDate, endDate, weekDaysOff) => {
    if (!isValid(startDate) || !isValid(endDate)) return 0;
    const offIndices = (weekDaysOff || []).map(d => getJsDayIndex(d)).filter(i => i !== -1);
    const workingDays = eachDayOfInterval({ start: startDate, end: endDate })
        .filter(day => !offIndices.includes(getDay(day)));
    return workingDays.length;
};

export const calculateLiveAuditionSeats = (watchedScheduleForm, dateSpecificOffSlots, countWorkingDays, isCurrentVideo, setValue) => {
    if (isCurrentVideo) return { perDay: 0, total: 0 }; 
    const [startDate, endDate, startTime, endTime, castingTime, weekDaysOff] = watchedScheduleForm; // This is still an array here in StepSchedule

    if (!startDate || !endDate || !startTime || !endTime || !castingTime?.value) {
        return { perDay: 0, total: 0 }; 
    }
    try {
        const startT = startTime instanceof Date ? startTime : parse(startTime, 'HH:mm', new Date());
        const endT = endTime instanceof Date ? endTime : parse(endTime, 'HH:mm', new Date());
        
        const totalMinutes = differenceInMinutes(endT, startT);
        const interval = parseInt(castingTime.value);
        if (totalMinutes <= 0 || !interval) return { perDay: 0, total: 0 };
        
        const slotsPerDay = Math.floor(totalMinutes / interval);
        const totalWorkingDays = countWorkingDays(startDate, endDate, weekDaysOff);

        let manualBreaksCount = 0;
        eachDayOfInterval({ start: startDate, end: endDate }).forEach(day => {
            const dateKey = format(day, 'yyyy-MM-dd');
            manualBreaksCount += (dateSpecificOffSlots[dateKey] || []).length;
        });
        
        const totalSeats = (slotsPerDay * totalWorkingDays) - manualBreaksCount;
        setTimeout(() => setValue('role_required_count', totalSeats > 0 ? totalSeats : 0), 0);
        return { perDay: slotsPerDay, total: totalSeats };
    } catch (e) { 
        console.error("Error calculating live audition seats:", e);
        return { perDay: 0, total: 0 }; 
    }
};

export const calculateOnlineAuditionSeats = (watchedScheduleForm, countWorkingDays, isCurrentVideo, setValue) => {
    if (!isCurrentVideo) return { perDay: 0, total: 0 };
    const [startDate, endDate, , , , weekDaysOff, , , onlineSeatsPerDay] = watchedScheduleForm; // This is still an array here in StepSchedule

    if (!startDate || !endDate || !onlineSeatsPerDay) {
        setTimeout(() => setValue('role_required_count', 0), 0);
        return { perDay: 0, total: 0 };
    }
    const seatsPerDay = parseInt(onlineSeatsPerDay, 10);
    if (isNaN(seatsPerDay) || seatsPerDay < 0) {
        setTimeout(() => setValue('role_required_count', 0), 0);
        return { perDay: 0, total: 0 };
    }
    const totalWorkingDays = countWorkingDays(startDate, endDate, weekDaysOff);
    const totalSeats = seatsPerDay * totalWorkingDays;
    
    setTimeout(() => setValue('role_required_count', totalSeats), 0);
    return { perDay: seatsPerDay, total: totalSeats };
};

// --- FIX STARTS HERE ---
// Changed parameter from watchedScheduleForm (an array) to a destructuring of an object
export const getAllTimeSlots = ({ start_time: sTime, end_time: eTime, casting_time: cTime }, isCurrentVideo) => {
    if (isCurrentVideo) return [];
    
    // cTime should have a .value if it's a select option, otherwise assume it's the raw value
    const intervalValue = cTime?.value !== undefined ? cTime.value : cTime;

    if (!sTime || !eTime || !intervalValue) return [];
    try {
        const start = sTime instanceof Date ? sTime : parse(sTime, 'h:mm aa', new Date()); // Assuming 'h:mm aa' for FormDatePicker
        const end = eTime instanceof Date ? eTime : parse(eTime, 'h:mm aa', new Date()); // Assuming 'h:mm aa' for FormDatePicker
        const interval = parseInt(intervalValue) || 15; // Default to 15 if parsing fails or invalid

        if (!isValid(start) || !isValid(end) || interval <= 0 || interval > differenceInMinutes(end, start)) {
            console.warn("Invalid time inputs or interval for generating slots:", { sTime, eTime, intervalValue });
            return [];
        }

        return eachMinuteOfInterval({ start, end }, { step: interval }).map(t => format(t, 'HH:mm'));
    } catch (e) { 
        console.error("Error generating time slots:", e);
        return []; 
    }
};
// --- FIX ENDS HERE ---


export const createDefaultSchedule = (auditionTypeOption, projectDateRanges, projectNameValue, countWorkingDays) => {
    const isVideo = auditionTypeOption.label.toLowerCase().includes('online');
    const defaultProjectDates = projectDateRanges.get(projectNameValue) || {};
    const defaultStartDate = defaultProjectDates.projectStartDate || new Date();
    const defaultEndDate = defaultProjectDates.projectEndDate || addDays(new Date(), 30);
    const defaultWeekDaysOff = [];
    const defaultCastingLocation = isVideo ? 'Web Upload' : 'TBD';
    let defaultNumberOfSeats = 0;
    let defaultNumberOfSeatsPerDay = 0;

    if (isVideo) {
        const defaultWorkingDays = countWorkingDays(defaultStartDate, defaultEndDate, defaultWeekDaysOff);
        defaultNumberOfSeatsPerDay = 1; 
        defaultNumberOfSeats = defaultNumberOfSeatsPerDay * defaultWorkingDays;
    } else {
        const defaultTotalMinutes = differenceInMinutes(parse('19:00', 'HH:mm', new Date()), parse('10:00', 'HH:mm', new Date()));
        const defaultSlotsPerDay = Math.floor(defaultTotalMinutes / 15);
        const defaultWorkingDays = countWorkingDays(defaultStartDate, defaultEndDate, defaultWeekDaysOff);
        defaultNumberOfSeatsPerDay = defaultSlotsPerDay;
        defaultNumberOfSeats = defaultSlotsPerDay * defaultWorkingDays;
    }

    return {
        auditionType: auditionTypeOption.value,
        startDate: format(defaultStartDate, 'yyyy-MM-dd'),
        endDate: format(defaultEndDate, 'yyyy-MM-dd'),
        startTime: '10:00',
        endTime: '19:00',
        castingTime: '15',
        weekDaysOff: defaultWeekDaysOff.map(x => x.label).join(';'),
        numberOfSeats: defaultNumberOfSeats,
        numberOfSeatsPerDay: defaultNumberOfSeatsPerDay,
        up_offSlotsData: '{}',
        castingLocation: defaultCastingLocation
    };
};

export const toMultiSelectOptions = (valueString, lookupOptionsArray) => 
    valueString ? valueString.split(';').map(val => lookupOptionsArray?.find(opt => opt.value === val)).filter(Boolean) : [];
export const getJsDayIndex = (dayInput) => {
    if (dayInput === null || dayInput === undefined) return -1;
    let str = typeof dayInput === 'object' ? String(dayInput.label || dayInput.value || '') : String(dayInput);
    str = str.trim().toLowerCase();
    const nameMap = {
        'sunday': 0, 'sun': 0,
        'monday': 1, 'mon': 1,
        'tuesday': 2, 'tue': 2,
        'wednesday': 3, 'wed': 3,
        'thursday': 4, 'thu': 4,
        'friday': 5, 'fri': 5,
        'saturday': 6, 'sat': 6
    };
    if (nameMap[str] !== undefined) return nameMap[str];
    if (!isNaN(str) && str !== '') {
        const num = parseInt(str, 10);
        if (num === 7) return 0;
        return num % 7;
    }
    return -1;
};
// ✅ Concise Helper: Safely parses Week Days Off for Create & Edit Mode
export const parseWeekDaysOff = (input) => {
    if (!input) return [];
    const items = Array.isArray(input) ? input : (typeof input === 'string' ? input.split(';') : [input]);
    return STATIC_OPTIONS.week_days.filter(d => 
        items.some(item => {
            const idx = getJsDayIndex(item);
            return idx !== -1 ? idx === getJsDayIndex(d) : String(d.value) === String(item?.value || item).trim();
        })
    );
};